import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/mock';
import { Button } from '../components/ui/button';
import { Newspaper, BookOpen, Calendar, ArrowRight, ArrowLeft, Globe, Menu } from 'lucide-react';

const Magazine = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="nav-modern fixed w-full top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="logo-container">
                <img 
                  src="https://i.imgur.com/1efIyiz.jpeg" 
                  alt="Tamkeen Logo" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div className="logo-fallback" style={{ display: 'none' }}>ت</div>
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-800 tracking-tight">
                  {language === 'ar' ? 'شركة التمكين' : 'Tamkeen Company'}
                </h1>
                <p className="text-sm text-gray-600 font-medium">
                  {t.footer.tagline}
                </p>
              </div>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              <a href="/" className="nav-link">{t.nav.home}</a>
              <a href="/#services" className="nav-link">{t.nav.services}</a>
              <a href="/#about" className="nav-link">{t.nav.about}</a>
              <a href="/#team" className="nav-link">{t.nav.team}</a>
              <a href="/magazine" className="nav-link text-yellow-600 font-bold">{t.nav.magazine}</a>
              <a href="/#contact" className="nav-link">{t.nav.contact}</a>
              <Button onClick={toggleLanguage} variant="outline" size="sm" className="language-switcher">
                <Globe className="w-4 h-4 mr-2" />
                {language === 'ar' ? 'EN' : 'عربي'}
              </Button>
            </div>
            
            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              <Button onClick={toggleLanguage} variant="outline" size="sm">
                <Globe className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm">
                <Menu className="w-6 h-6" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="gradient-bg py-20 pt-32">
        <div className="container mx-auto px-6">
          <div className="text-center text-white">
            <h1 className="text-5xl font-black mb-6">{t.magazine.title}</h1>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto">
              {t.magazine.description}
            </p>
          </div>
        </div>
      </section>

      {/* Main Sections */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Legal Analysis Section */}
            <div className="modern-card p-8 floating-card">
              <div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-yellow-400" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
                {t.magazine.legalAnalysis}
              </h2>
              <p className="text-gray-600 text-center mb-8 leading-relaxed">
                {t.magazine.legalAnalysisDesc}
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="bg-slate-50 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm text-gray-500">{language === 'ar' ? 'قريباً' : 'Coming Soon'}</span>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">
                    {language === 'ar' ? 'تحليل قانوني شامل' : 'Comprehensive Legal Analysis'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {language === 'ar' 
                      ? 'مقالات تحليلية معمقة حول القوانين الجديدة والتعديلات القانونية'
                      : 'In-depth analytical articles on new laws and legal amendments'}
                  </p>
                </div>

                <div className="bg-slate-50 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm text-gray-500">{language === 'ar' ? 'قريباً' : 'Coming Soon'}</span>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">
                    {language === 'ar' ? 'شرح القوانين الحديثة' : 'Modern Laws Explanation'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {language === 'ar' 
                      ? 'شرح تفصيلي للقوانين والتشريعات الحديثة وتأثيرها'
                      : 'Detailed explanation of modern laws and legislation and their impact'}
                  </p>
                </div>
              </div>

              <Button className="btn-primary-modern w-full" disabled>
                {t.magazine.comingSoon}
              </Button>
            </div>

            {/* News & Legislation Section */}
            <div className="modern-card p-8 floating-card">
              <div className="w-20 h-20 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Newspaper className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
                {t.magazine.newsLegislation}
              </h2>
              <p className="text-gray-600 text-center mb-8 leading-relaxed">
                {t.magazine.newsLegislationDesc}
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="bg-yellow-50 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm text-gray-500">{language === 'ar' ? 'قريباً' : 'Coming Soon'}</span>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">
                    {language === 'ar' ? 'أخبار القوانين الحديثة' : 'Modern Laws News'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {language === 'ar' 
                      ? 'آخر الأخبار حول القوانين والتعليمات الجديدة وقرارات المحاكم'
                      : 'Latest news about new laws, instructions, and court decisions'}
                  </p>
                </div>

                <div className="bg-yellow-50 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm text-gray-500">{language === 'ar' ? 'قريباً' : 'Coming Soon'}</span>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">
                    {language === 'ar' ? 'فعاليات الشركة' : 'Company Events'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {language === 'ar' 
                      ? 'أخبار عن فعاليات الشركة والندوات والمشاركات المجتمعية'
                      : 'News about company events, seminars, and community participation'}
                  </p>
                </div>
              </div>

              <Button className="btn-secondary-modern w-full" disabled>
                {t.magazine.comingSoon}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="container mx-auto px-6 text-center">
          <Button onClick={() => navigate('/')} className="btn-primary-modern text-lg px-8 py-4">
            {language === 'ar' ? (
              <span className="flex items-center gap-2">
                {t.nav.home}
                <ArrowLeft className="w-5 h-5" />
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <ArrowRight className="w-5 h-5" />
                {t.nav.home}
              </span>
            )}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Magazine;
